import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Skeleton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { NotFound } from '../layout/SystemState';
import {
    addRunLessons,
    createRun,
    getAssignment,
    getRun,
    getRunDefaults,
    listAdjudicators,
    listCandidateLessons,
    updateRunSettings
} from '../../services/runService';
import { BackToRuns, Card, PageTitle, RunStatusChip, WizardSteps, fmt, shortDate } from './parts';

const SETUP_STEPS = ['Settings', 'Lessons', 'Assign'];
const ADD_STEPS = ['Lessons', 'Assign'];
const short = (email) => (email ? email.split('@')[0] : '—');

const Section = ({ title, children }) => (
    <Box component="section" aria-label={title} sx={{ '& + &': { mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' } }}>
        <Typography variant="overline" component="h2" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{title}</Typography>
        {children}
    </Box>
);

const FlaggingNote = ({ flagging }) => (flagging ? (
    <Typography variant="body2" color="text.secondary">
        CD and HU thresholds per source, set from Study 1’s results: classifier CD {flagging.classifier?.cd ?? '—'} · HU {flagging.classifier?.hu ?? '—'}; LLM CD {flagging.llm?.cd ?? '—'} · HU {flagging.llm?.hu ?? '—'}.
    </Typography>
) : (
    <Alert severity="warning" icon={false}>
        CD and HU thresholds per source (classifier, LLM) default to the values set from Study 1’s results. Not set yet: this run cannot start until they are.
    </Alert>
));

const Percent = ({ label, help, value, onChange }) => (
    <Box>
        <TextField
            label={label}
            type="number"
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value === '' ? '' : Math.max(0, Math.min(100, Math.round(Number(e.target.value)))))}
            inputProps={{ min: 0, max: 100 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            sx={{ width: 220 }}
        />
        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.5, maxWidth: 360 }}>{help}</Typography>
    </Box>
);

// 1 · Settings (wf-research-run-setup-1): name, schedule, flagging, control share, reliability target,
// the unsure rule and the slots. Fixed once the first lesson is saved.
const SettingsStep = ({ initial, runId, onDone }) => {
    const [defaults, setDefaults] = useState(null);
    const [accounts, setAccounts] = useState(null);
    const [failed, setFailed] = useState(false);
    const [form, setForm] = useState(initial || null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([getRunDefaults(), listAdjudicators()])
            .then(([d, a]) => {
                setDefaults(d);
                setAccounts(a);
                setForm((f) => f || {
                    name: '',
                    schedule: '',
                    controlShare: d.controlShare,
                    reliabilityTarget: d.reliabilityTarget,
                    tieOnUnsure: d.tieOnUnsure,
                    slots: ['A1', 'A2', 'A3', 'A4'].map((name) => ({ name, userId: '' })),
                });
            })
            .catch(() => setFailed(true));
    }, []);

    if (failed) return <Alert severity="error">Couldn’t load the run defaults or the annotator accounts.</Alert>;
    if (!form || !accounts) return <Skeleton variant="rounded" height={480} />;

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const setSlot = (i, userId) => set('slots', form.slots.map((s, j) => (j === i ? { ...s, userId } : s)));
    const used = new Set(form.slots.map((s) => s.userId).filter(Boolean));
    const unmapped = form.slots.filter((s) => !s.userId).map((s) => s.name);
    const blocked = !form.name.trim() ? 'Name the run first' : unmapped.length ? `Map ${unmapped.join(', ')} first` : form.controlShare === '' || form.reliabilityTarget === '' ? 'Fill in both shares first' : null;

    const submit = async () => {
        setBusy(true);
        setError(null);
        const body = {
            name: form.name.trim(),
            controlShare: form.controlShare,
            reliabilityTarget: form.reliabilityTarget,
            tieOnUnsure: form.tieOnUnsure,
            slots: form.slots.map(({ name, userId }) => ({ name, userId })),
            ...(form.schedule.trim() ? { schedule: form.schedule.trim() } : {}),
        };
        try {
            const id = runId ? (await updateRunSettings(runId, body), runId) : await createRun(body);
            onDone(id, { ...form, accounts });
        } catch (err) {
            setBusy(false);
            setError(err.response?.status === 409
                ? 'Settings are fixed once the first lesson is added. Nothing was changed.'
                : err.response?.data?.error || 'Couldn’t save the run. Nothing was saved. Try again.');
        }
    };

    return (
        <Card>
            <Section title="Run">
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField label="Name" required size="small" value={form.name} onChange={(e) => set('name', e.target.value)} inputProps={{ maxLength: 120 }} sx={{ flex: '1 1 320px', maxWidth: 480 }} />
                    <Box sx={{ flex: '1 1 240px', maxWidth: 320 }}>
                        <TextField label="Schedule (optional)" size="small" fullWidth value={form.schedule} onChange={(e) => set('schedule', e.target.value)} inputProps={{ maxLength: 200 }} />
                        <Typography variant="caption" color="text.secondary">Shown as typed on My queue.</Typography>
                    </Box>
                </Box>
            </Section>
            <Section title="Flagging">
                <FlaggingNote flagging={defaults.flagging} />
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2.5 }}>
                    <Percent label="Control items per lesson" help="Random unflagged utterances, drawn when a lesson’s flags are computed." value={form.controlShare} onChange={(v) => set('controlShare', v)} />
                    <Percent label="Reliability subset target" help="Share of lessons to double-code (HD). You choose which lessons; the page shows progress." value={form.reliabilityTarget} onChange={(v) => set('reliabilityTarget', v)} />
                </Box>
                <FormControlLabel sx={{ mt: 1.5 }} control={<Switch checked={form.tieOnUnsure} onChange={(e) => set('tieOnUnsure', e.target.checked)} />} label="An unsure mark counts as a tie" />
            </Section>
            <Section title="Adjudicators">
                <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 1.5 }}>Slots name adjudicators in the export; each slot is one annotator account.</Typography>
                <Box sx={{ display: 'grid', gap: 1.25, maxWidth: 480 }}>
                    {form.slots.map((slot, i) => (
                        <Box key={slot.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography sx={{ width: 32, fontWeight: 700 }}>{slot.name}</Typography>
                            <FormControl size="small" fullWidth>
                                <Select
                                    displayEmpty
                                    value={slot.userId}
                                    onChange={(e) => setSlot(i, e.target.value)}
                                    inputProps={{ 'aria-label': `Account for ${slot.name}` }}
                                    renderValue={(id) => (id ? accounts.find((a) => a.id === id)?.email : <Box component="span" sx={{ color: 'warning.dark' }}>Choose an annotator account</Box>)}
                                    sx={{ '& fieldset': !slot.userId ? { borderColor: 'warning.main' } : {} }}
                                >
                                    {accounts.map((a) => <MenuItem key={a.id} value={a.id} disabled={used.has(a.id) && slot.userId !== a.id}>{a.email}{a.name ? ` · ${a.name}` : ''}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                    ))}
                </Box>
                <Button startIcon={<Add />} onClick={() => set('slots', [...form.slots, { name: `A${form.slots.length + 1}`, userId: '' }])} disabled={form.slots.length >= accounts.length} sx={{ mt: 1 }}>
                    Add a slot
                </Button>
            </Section>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Box sx={{ textAlign: 'right' }}>
                    <Button variant="contained" size="large" onClick={submit} disabled={busy || Boolean(blocked)} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                        {runId ? 'Save settings: add lessons' : 'Create run: add lessons'}
                    </Button>
                    {blocked && <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.75 }}>{blocked}</Typography>}
                </Box>
            </Box>
        </Card>
    );
};

const labelsText = ({ classifier, llm }) => {
    const part = (name, s) => (s === 'ready' ? name : `${name} ${s}`);
    return classifier === 'ready' && llm === 'ready' ? 'Classifier + LLM' : `${part('Classifier', classifier)}; ${part('LLM', llm)}`;
};

// 2 · Add lessons (wf-research-run-setup-2): consented, analysed lessons in no run; nothing is saved here
const LessonsStep = ({ runId, selected, onCancel, onBack, onContinue }) => {
    const [lessons, setLessons] = useState(null);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [ticked, setTicked] = useState(new Set(selected.map((l) => l.lessonId)));

    const load = useCallback(() => {
        setFailed(false);
        listCandidateLessons(runId).then(setLessons).catch(() => setFailed(true));
    }, [runId]);
    useEffect(load, [load]);

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (lessons || []).filter((l) => !q || [l.code, l.name, l.subject].some((v) => (v || '').toLowerCase().includes(q)));
    }, [lessons, query]);
    const picked = (lessons || []).filter((l) => ticked.has(l.lessonId));
    const toggle = (id) => setTicked((t) => { const n = new Set(t); if (n.has(id)) n.delete(id); else n.add(id); return n; });

    return (
        <Card title="Add lessons">
            <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 2 }}>Consented, machine-labelled lessons not in any run. Teacher names are not shown.</Typography>
            {failed && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the lessons.</Alert>}
            {lessons && lessons.length === 0 && <Alert severity="info" icon={false}>No consented lessons are waiting.</Alert>}
            {!failed && (!lessons || lessons.length > 0) && (
                <>
                    <TextField
                        size="small"
                        placeholder="Search by lesson ID or subject"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        inputProps={{ 'aria-label': 'Search by lesson ID or subject' }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        sx={{ width: 340, maxWidth: '100%', mb: 1.5 }}
                    />
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" aria-label="Candidate lessons" sx={{ minWidth: 640 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    {['Lesson', 'Subject · grade', 'Utterances', 'Uploaded', 'Machine labels'].map((h) => <TableCell key={h}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!lessons && [0, 1, 2].map((i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton /></TableCell></TableRow>)}
                                {shown.map((l) => (
                                    <TableRow key={l.lessonId} hover selected={ticked.has(l.lessonId)} onClick={() => toggle(l.lessonId)} sx={{ cursor: 'pointer' }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={ticked.has(l.lessonId)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(l.lessonId)} inputProps={{ 'aria-label': `Select ${l.code}` }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.code}</Typography>
                                            {l.name && <Typography variant="caption" color="text.secondary">{l.name}</Typography>}
                                        </TableCell>
                                        <TableCell>{[l.subject, l.gradeLevel].filter(Boolean).join(' · ') || '—'}</TableCell>
                                        <TableCell>{fmt(l.utteranceCount)}</TableCell>
                                        <TableCell>{shortDate(l.uploadedAt)}</TableCell>
                                        <TableCell sx={{ color: l.machineLabels.classifier === 'ready' && l.machineLabels.llm === 'ready' ? 'text.primary' : 'warning.dark' }}>{labelsText(l.machineLabels)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>A lesson whose LLM labels are still running can be added; its flags wait for them.</Typography>
                </>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 3 }}>
                <Typography variant="body2" role="status" sx={{ fontWeight: 600 }}>
                    {picked.length ? `${picked.length} ${picked.length === 1 ? 'lesson' : 'lessons'} selected · ${fmt(picked.reduce((n, l) => n + l.utteranceCount, 0))} utterances` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {onBack && <Button variant="outlined" onClick={onBack}>Back to Settings</Button>}
                    <Button variant="outlined" onClick={onCancel}>Cancel</Button>
                    <Button variant="contained" size="large" disabled={!picked.length} onClick={() => onContinue(picked)}>
                        Continue: assign {picked.length || ''} {picked.length === 1 ? 'lesson' : 'lessons'}
                    </Button>
                </Box>
            </Box>
        </Card>
    );
};

// Why a new row can't be saved, or null; `who` names an account
const problemOf = (row, slotAccount, who) => {
    if (row.reliability && row.ann2 && row.ann2 === row.ann) return { field: 'ann2', text: 'Same annotator twice' };
    if (row.a && row.b && row.a === row.b) return { field: 'b', text: 'Same slot twice' };
    if (row.t && (row.t === row.a || row.t === row.b)) return { field: 't', text: 'Tie-breaker adjudicates it' };
    const annotators = [row.ann, row.reliability && row.ann2].filter(Boolean);
    for (const field of ['a', 'b', 't']) {
        const slot = row[field];
        if (slot && annotators.includes(slotAccount(slot)?.id)) {
            return { field, text: `${who(slotAccount(slot).id)} is ${slotAccount(slot).slotName}: can’t adjudicate a lesson they annotate` };
        }
    }
    return null;
};
const complete = (row) => Boolean(row.ann && (!row.reliability || row.ann2) && row.a && row.b && row.t);

const Picker = ({ label, value, options, onChange, problem, disabled }) => (
    <FormControl size="small" sx={{ minWidth: 112 }} error={Boolean(problem)}>
        <Select
            displayEmpty
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ 'aria-label': label }}
            renderValue={(v) => (v ? options.find((o) => o.value === v)?.label : <Box component="span" sx={{ color: disabled ? 'text.disabled' : 'warning.dark' }}>{disabled ? '—' : 'Choose'}</Box>)}
            sx={{ '& fieldset': !value && !problem && !disabled ? { borderColor: 'warning.main' } : {} }}
        >
            {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}{o.detail ? ` · ${o.detail}` : ''}</MenuItem>)}
        </Select>
    </FormControl>
);

// 3 · Assign (wf-research-run-setup-3): one row per new lesson with its annotator(s), two adjudicators
// and a tie-breaker; the load in this run and the reliability share in view. Saving starts a run in setup.
const AssignStep = ({ runId, run, picked, reliabilityTarget, onBack, onSaved }) => {
    const [data, setData] = useState(null);
    const [accounts, setAccounts] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [failed, setFailed] = useState(false);
    const [rows, setRows] = useState(() => Object.fromEntries(picked.map((l) => [l.lessonId, { reliability: false, ann: '', ann2: '', a: '', b: '', t: '' }])));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        setFailed(false);
        Promise.all([getAssignment(runId), listAdjudicators(), getRunDefaults()])
            .then(([a, acc, d]) => { setData(a); setAccounts(acc); setDefaults(d); })
            .catch(() => setFailed(true));
    }, [runId]);
    useEffect(load, [load]);

    const slots = useMemo(() => data?.slots || [], [data]);
    const accountOf = useCallback((value) => accounts?.find((a) => a.id === value || a.email === value), [accounts]);
    const slotAccount = useCallback((slotId) => {
        const slot = slots.find((s) => s.id === slotId);
        const account = slot && accountOf(slot.account);
        return account ? { ...account, slotName: slot.name } : null;
    }, [slots, accountOf]);
    const who = useCallback((id) => short(accountOf(id)?.email), [accountOf]);

    if (failed) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the run’s assignment.</Alert>;
    if (!data || !accounts || !defaults) return <Skeleton variant="rounded" height={420} />;

    const setCell = (id, key, value) => setRows((r) => ({ ...r, [id]: { ...r[id], [key]: value, ...(key === 'reliability' && !value ? { ann2: '' } : {}) } }));
    const problems = picked.map((l) => ({ lesson: l, problem: problemOf(rows[l.lessonId], slotAccount, who) })).filter((p) => p.problem);
    const incomplete = picked.filter((l) => !complete(rows[l.lessonId]));
    const thresholdsMissing = run.status === 'setup' && !defaults.flagging;

    const saved = data.lessons;
    const relCount = saved.filter((l) => l.reliability).length + picked.filter((l) => rows[l.lessonId].reliability).length;
    const total = saved.length + picked.length;
    const target = reliabilityTarget ?? defaults.reliabilityTarget;

    // Load per person: lessons they annotate, adjudicate and break ties for (saved and new rows), and
    // queued items of saved, flagged lessons they adjudicate
    const load2 = {};
    const bump = (id, key, n = 1) => { if (!id) return; load2[id] = load2[id] || { annotate: 0, adjudicate: 0, tie: 0, items: 0 }; load2[id][key] += n; };
    saved.forEach((l) => {
        (l.annotatorIds || []).forEach((id) => bump(accountOf(id)?.id || id, 'annotate'));
        l.blindSlotIds.forEach((s) => { bump(slotAccount(s)?.id, 'adjudicate'); bump(slotAccount(s)?.id, 'items', l.items); });
        bump(slotAccount(l.tieBreakSlotId)?.id, 'tie');
    });
    picked.forEach((l) => {
        const r = rows[l.lessonId];
        [r.ann, r.reliability && r.ann2].filter(Boolean).forEach((id) => bump(id, 'annotate'));
        [r.a, r.b].filter(Boolean).forEach((s) => bump(slotAccount(s)?.id, 'adjudicate'));
        bump(r.t && slotAccount(r.t)?.id, 'tie');
    });
    const people = [
        ...slots.map((s) => ({ id: slotAccount(s.id)?.id, label: `${short(slotAccount(s.id)?.email)} · ${s.name}` })),
        ...Object.keys(load2).filter((id) => !slots.some((s) => slotAccount(s.id)?.id === id)).map((id) => ({ id, label: who(id) })),
    ];

    const blocked = thresholdsMissing
        ? 'Flag thresholds are not set yet'
        : problems.length ? `Fix ${problems.map((p) => p.lesson.code).join(', ')} first`
            : incomplete.length ? `Assign ${incomplete.map((l) => l.code).join(', ')} first` : null;

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await addRunLessons(runId, picked.map((l) => {
                const r = rows[l.lessonId];
                return { lessonId: l.lessonId, reliability: r.reliability, annotatorIds: r.reliability ? [r.ann, r.ann2] : [r.ann], blindSlotIds: [r.a, r.b], tieBreakSlotId: r.t };
            }));
            onSaved();
        } catch (err) {
            setBusy(false);
            const status = err.response?.status;
            setError(status === 409
                ? (err.response?.data?.error || 'A lesson was added to another run or lost its consent meanwhile. Nothing was saved. Go back to Lessons to pick again.')
                : status === 400 ? (err.response?.data?.error || 'A row can’t be saved. Nothing was saved.') : 'Couldn’t save. Nothing was saved. Try again.');
        }
    };

    const annotatorOptions = accounts.map((a) => ({ value: a.id, label: short(a.email) }));
    const slotOptions = slots.map((s) => ({ value: s.id, label: s.name, detail: short(slotAccount(s.id)?.email) }));

    return (
        <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12}>
                <Card title="New lessons · one row each">
                    <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 2 }}>
                        Annotators are accounts; adjudicators and the tie-breaker are the run’s slots. Nobody may adjudicate a lesson they annotate.
                    </Typography>
                    {thresholdsMissing && <Box sx={{ mb: 2 }}><FlaggingNote flagging={null} /></Box>}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" aria-label="New lessons" sx={{ minWidth: 820 }}>
                            <TableHead>
                                <TableRow>{['Lesson', 'Reliability', 'Annotator', '2nd annotator', 'Adjudicator 1', 'Adjudicator 2', 'Tie-breaker'].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                            </TableHead>
                            <TableBody>
                                {picked.map((l) => {
                                    const r = rows[l.lessonId];
                                    const problem = problemOf(r, slotAccount, who);
                                    return (
                                        <React.Fragment key={l.lessonId}>
                                            <TableRow sx={{ '& td': problem ? { borderBottom: 0 } : {} }}>
                                                <TableCell sx={{ fontWeight: 700 }}>{l.code}</TableCell>
                                                <TableCell padding="checkbox"><Checkbox checked={r.reliability} onChange={(e) => setCell(l.lessonId, 'reliability', e.target.checked)} inputProps={{ 'aria-label': `${l.code} in the reliability subset` }} /></TableCell>
                                                <TableCell><Picker label={`Annotator for ${l.code}`} value={r.ann} options={annotatorOptions} onChange={(v) => setCell(l.lessonId, 'ann', v)} /></TableCell>
                                                <TableCell><Picker label={`2nd annotator for ${l.code}`} value={r.ann2} options={annotatorOptions} onChange={(v) => setCell(l.lessonId, 'ann2', v)} disabled={!r.reliability} problem={problem?.field === 'ann2'} /></TableCell>
                                                {['a', 'b', 't'].map((k, i) => (
                                                    <TableCell key={k}>
                                                        <Picker label={`${['Adjudicator 1', 'Adjudicator 2', 'Tie-breaker'][i]} for ${l.code}`} value={r[k]} options={slotOptions} onChange={(v) => setCell(l.lessonId, k, v)} problem={problem?.field === k} />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            {problem && (
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ pt: 0, color: 'error.main', typography: 'caption' }} role="alert">{problem.text}</TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Alert severity={total && Math.round((relCount / total) * 100) < target ? 'info' : 'success'} icon={false} sx={{ mt: 2 }}>
                        Reliability subset: {relCount} of {total} lessons ({total ? Math.round((relCount / total) * 100) : 0}%) with this save · target {target}%. Tick a lesson to give it a second annotator, blind to the first.
                    </Alert>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mt: 3 }}>
                        <Button variant="outlined" onClick={onBack}>Back</Button>
                        <Box sx={{ textAlign: 'right' }}>
                            <Button variant="contained" size="large" onClick={save} disabled={busy || Boolean(blocked)} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                                {run.status === 'setup' ? 'Save and start the run' : 'Save'}
                            </Button>
                            {blocked && <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.75 }}>{blocked}</Typography>}
                        </Box>
                    </Box>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card title="Load in this run">
                    <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="Load in this run" sx={{ minWidth: 560 }}>
                        <TableHead>
                            <TableRow>{['Person', 'Lessons to annotate', 'Lessons to adjudicate', 'Tie-breaker for', 'Queued items now'].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                        </TableHead>
                        <TableBody>
                            {people.map((p) => {
                                const n = load2[p.id] || { annotate: 0, adjudicate: 0, tie: 0, items: 0 };
                                return (
                                    <TableRow key={p.label}>
                                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{p.label}</TableCell>
                                        <TableCell>{n.annotate}</TableCell>
                                        <TableCell>{n.adjudicate}</TableCell>
                                        <TableCell>{n.tie}</TableCell>
                                        <TableCell>{fmt(n.items)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </TableContainer>
                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                        Adjudication load counts lessons until a lesson’s flags exist; queued items count only lessons already flagged.
                    </Typography>
                </Card>
            </Grid>
        </Grid>
    );
};

// /runs/new, /runs/:runId/setup (an ADAS run) and /runs/:runId/add (wf-research-run-setup 1–3, YES-267):
// Settings, Lessons, Assign. A lesson is in the run only once its row is saved; Add lessons on an open or
// closed run opens Lessons and Assign only.
const AdasRunWizard = ({ mode }) => {
    const { runId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [settings, setSettings] = useState(location.state?.settings || null);
    const [run, setRun] = useState(null);
    const [status, setStatus] = useState(mode === 'new' ? 'ready' : 'loading');
    const [step, setStep] = useState(mode === 'new' ? 'settings' : 'lessons');
    const [picked, setPicked] = useState([]);

    const load = useCallback(async () => {
        if (!runId) return;
        setStatus('loading');
        try {
            const r = await getRun(runId);
            setRun(r);
            setStatus('ready');
        } catch (err) {
            setStatus(err.response?.status === 404 ? 'notFound' : 'error');
        }
    }, [runId]);
    useEffect(() => { load(); }, [load]);

    if (status === 'notFound') return <NotFound />;
    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the run.</Alert>
            </Box>
        );
    }

    const steps = mode === 'add' ? ADD_STEPS : SETUP_STEPS;
    const active = steps.indexOf(step === 'settings' ? 'Settings' : step === 'lessons' ? 'Lessons' : 'Assign');
    const summary = () => navigate(`/runs/${encodeURIComponent(runId)}`, { replace: true });
    // Settings change only before any lesson is saved, and only where this page knows them
    const canEditSettings = mode === 'setup' && settings && run?.status === 'setup' && !run?.lessons;

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <BackToRuns />
            <PageTitle>
                <Typography variant="h2" component="h1">
                    {mode === 'new' && step === 'settings' ? 'New run from lessons' : run ? run.name : settings?.name || <Skeleton width={300} />}
                </Typography>
                {run && <RunStatusChip status={run.status} />}
            </PageTitle>
            <WizardSteps steps={steps} active={active} />

            {step === 'settings' && (
                <SettingsStep
                    initial={settings}
                    runId={mode === 'new' ? null : runId}
                    onDone={(id, form) => {
                        setSettings(form);
                        if (mode === 'new') navigate(`/runs/${encodeURIComponent(id)}/setup`, { replace: true, state: { settings: form } });
                        else { setStep('lessons'); load(); }
                    }}
                />
            )}
            {step === 'lessons' && run && (
                <LessonsStep
                    runId={runId}
                    selected={picked}
                    onBack={canEditSettings ? () => setStep('settings') : null}
                    onCancel={() => (mode === 'add' ? summary() : navigate('/runs'))}
                    onContinue={(lessons) => { setPicked(lessons); setStep('assign'); }}
                />
            )}
            {step === 'assign' && run && (
                <AssignStep
                    runId={runId}
                    run={run}
                    picked={picked}
                    reliabilityTarget={settings?.reliabilityTarget}
                    onBack={() => setStep('lessons')}
                    onSaved={summary}
                />
            )}
        </Box>
    );
};

export default AdasRunWizard;
